import { REGIONS } from '../../utils/constants'
import { setBackGroundColor, setBold, setBorder, setCellNumberFormat } from './common'

const importantColor = 'FAC090'
const rateColor = 'FFC000'
const contentColor = 'FFFF99'
export const defaultColumnsCount = 2

const getExcelColumnName = (index) => {
  let name = ''
  let num = index
  while (num >= 0) {
    name = String.fromCharCode(65 + (num % 26)) + name
    num = Math.floor(num / 26) - 1
  }
  return name
}

const generateAllColumns = (count) => {
  const columns = []
  for (let i = 0; i < count; i++) {
    columns.push(getExcelColumnName(i))
  }
  return columns
}

export const allColumns = generateAllColumns(300)

export const getColumnLayout = (columnsCount = defaultColumnsCount) => {
  const regionsCount = REGIONS.length

  const rateStart = 1
  const rateEnd = rateStart + columnsCount - 1
  const totalRateIdx = rateEnd + 1
  const regionsStart = totalRateIdx + 1
  const regionsEnd = regionsStart + regionsCount - 1
  const totalGeneralIdx = regionsEnd + 1
  const separatorIdx = totalGeneralIdx + 1
  const accordsRepeatIdx = separatorIdx + 1

  const rfdBlocks = []
  let currentIdx = accordsRepeatIdx + 1
  for (let i = 0; i < columnsCount; i++) {
    rfdBlocks.push({
      start: currentIdx,
      end: currentIdx + regionsCount - 1,
      totalIdx: currentIdx + regionsCount,
    })
    currentIdx += regionsCount + 1
  }

  const finalBlockStart = currentIdx
  const finalBlockEnd = finalBlockStart + regionsCount - 1
  const finalTotalIdx = finalBlockEnd + 1

  const totalCellsCount = finalTotalIdx + 1

  const rateCells = []
  for (let i = rateStart; i <= totalRateIdx; i++) {
    rateCells.push(allColumns[i])
  }

  const totalCells = [allColumns[totalGeneralIdx]]
  rfdBlocks.forEach((block) => totalCells.push(allColumns[block.totalIdx]))
  totalCells.push(allColumns[finalTotalIdx])

  return {
    cells: allColumns.slice(0, totalCellsCount),
    rateCells,
    totalCells,
    rateStart,
    rateEnd,
    totalRateIdx,
    regionsStart,
    regionsEnd,
    totalGeneralIdx,
    separatorIdx,
    accordsRepeatIdx,
    rfdBlocks,
    finalBlockStart,
    finalBlockEnd,
    finalTotalIdx,
  }
}

export const setColumnsWidth = (worksheet, columnsCount) => {
  const layout = getColumnLayout(columnsCount)
  worksheet.columns = layout.cells.map((_cell, idx) =>
    idx === 0 || idx === layout.accordsRepeatIdx ? { width: 60 } : { width: 12 }
  )
}

export const getUnlockedCellsForRow = (rowNumber, columnsCount) => {
  const layout = getColumnLayout(columnsCount)
  const cells = []
  for (let i = layout.regionsStart; i <= layout.regionsEnd; i++) {
    cells.push(`${allColumns[i]}${rowNumber}`)
  }
  return cells
}

export const addHeaderRow = (worksheet, rowNumber, columnsCount) => {
  const layout = getColumnLayout(columnsCount)
  worksheet.addRow(layout.cells.map(() => ''))
  setBold(worksheet, rowNumber, true)
  worksheet.getRow(rowNumber).alignment = { vertical: 'middle', horizontal: 'center' }
}

export const addTableHeader = (worksheet, row, columns = []) => {
  const sortedColumns = [...columns].sort((a, b) => a.position - b.position)
  const columnsCount = sortedColumns.length
  const layout = getColumnLayout(columnsCount)
  const regionCodes = REGIONS.map((r) => r.code)

  const columnNames =
    sortedColumns.length > 0 ? sortedColumns.map((col) => col.name) : ['Tx Coop filiales', 'Tx Coop SAS']

  const headerRow = ['Accords = Gamme', ...columnNames, 'Total', ...regionCodes, 'Total général', '', 'Accords = Gamme']

  sortedColumns.forEach((col) => {
    headerRow.push(...regionCodes)
    headerRow.push(`Total ${col.name}`)
  })

  if (sortedColumns.length === 0) {
    headerRow.push(...regionCodes, 'Total filiales')
    headerRow.push(...regionCodes, 'Total SAS')
  }

  headerRow.push(...regionCodes, 'Total')

  worksheet.addRow(headerRow)
  setBold(worksheet, row)
  worksheet.getRow(row).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }

  layout.cells.forEach((cell) => {
    if (cell === allColumns[layout.separatorIdx]) return
    setBorder(worksheet, `${cell}${row}`)
    if (layout.rateCells.includes(cell)) {
      setBackGroundColor(worksheet, `${cell}${row}`, rateColor)
    } else {
      setBackGroundColor(worksheet, `${cell}${row}`, importantColor)
    }
  })
}

export const addTableRow = (worksheet, coopGroup, amounts, row, columns = []) => {
  const sortedColumns = [...columns].sort((a, b) => a.position - b.position)
  const columnsCount = sortedColumns.length
  const layout = getColumnLayout(columnsCount)

  const sortedColumnValues = (coopGroup.columnValues || []).sort(
    (a, b) => (a.column?.position || 0) - (b.column?.position || 0)
  )

  const taxValues = []
  let totalTax = 0
  for (let i = 0; i < columnsCount; i++) {
    const rawValue = sortedColumnValues[i]?.value || 0
    const value = coopGroup.isPercent ? rawValue / 100 : rawValue
    taxValues.push(value)
    totalTax += value
  }

  const regionsStartCol = allColumns[layout.regionsStart]
  const regionsEndCol = allColumns[layout.regionsEnd]

  const rowData = [
    coopGroup.name,
    ...taxValues,
    totalTax,
    ...amounts,
    { formula: `SUM(${regionsStartCol}${row}:${regionsEndCol}${row})` },
    '',
    coopGroup.name,
  ]

  layout.rfdBlocks.forEach((block, blockIdx) => {
    const taxCol = allColumns[layout.rateStart + blockIdx]
    for (let i = 0; i < REGIONS.length; i++) {
      const regionCol = allColumns[layout.regionsStart + i]
      rowData.push({ formula: `${regionCol}${row}*$${taxCol}${row}` })
    }
    const blockStartCol = allColumns[block.start]
    const blockEndCol = allColumns[block.end]
    rowData.push({ formula: `SUM(${blockStartCol}${row}:${blockEndCol}${row})` })
  })

  for (let i = 0; i < REGIONS.length; i++) {
    const sumParts = layout.rfdBlocks.map((block) => `${allColumns[block.start + i]}${row}`)
    rowData.push({ formula: sumParts.join('+') })
  }
  const finalStartCol = allColumns[layout.finalBlockStart]
  const finalEndCol = allColumns[layout.finalBlockEnd]
  rowData.push({ formula: `SUM(${finalStartCol}${row}:${finalEndCol}${row})` })

  worksheet.addRow(rowData)

  layout.cells.forEach((cell) => {
    if (cell === allColumns[layout.separatorIdx]) return

    setBorder(worksheet, `${cell}${row}`)
    if (cell === 'A' || cell === allColumns[layout.accordsRepeatIdx]) return

    const currentCell = worksheet.getCell(`${cell}${row}`)
    if (layout.rateCells.includes(cell)) {
      currentCell.numFmt = coopGroup.isPercent ? '0.00%' : '0.00€'
    } else {
      setCellNumberFormat(worksheet, cell, row)
    }

    const backgroundColor =
      layout.rateCells.includes(cell) || layout.totalCells.includes(cell) ? rateColor : contentColor
    setBackGroundColor(worksheet, `${cell}${row}`, backgroundColor)
  })
}

export const addTableTotalRow = (worksheet, startingRow, row, columns = []) => {
  const columnsCount = columns.length
  const layout = getColumnLayout(columnsCount)

  const regionsStartCol = allColumns[layout.regionsStart]
  const regionsEndCol = allColumns[layout.regionsEnd]

  const excludedCells = [...layout.rateCells, allColumns[layout.separatorIdx], allColumns[layout.accordsRepeatIdx]]

  worksheet.addRow(
    layout.cells.map((cell) => {
      if (cell === 'A') return 'Total'
      if (excludedCells.includes(cell)) return ''
      if (cell === allColumns[layout.totalGeneralIdx]) {
        return { formula: `SUM(${regionsStartCol}${startingRow + 2}:${regionsEndCol}${row - 1})` }
      }
      return { formula: `SUM(${cell}${startingRow + 2}:${cell}${row - 1})` }
    })
  )

  setBold(worksheet, row)
  layout.cells.forEach((cell) => {
    if (cell === allColumns[layout.separatorIdx]) return
    setBorder(worksheet, `${cell}${row}`)
    if (layout.rateCells.includes(cell)) {
      setBackGroundColor(worksheet, `${cell}${row}`, rateColor)
    } else {
      setBackGroundColor(worksheet, `${cell}${row}`, importantColor)
      setCellNumberFormat(worksheet, cell, row)
    }
  })
}

export const getColumnsCount = (agreement) => {
  if (agreement.columns?.length > 0) return agreement.columns.length
  if (agreement.coopGroups?.[0]?.columnValues?.length > 0) return agreement.coopGroups[0].columnValues.length
  return 2
}
